import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MarktAkquise } from '../entities/MarktAkquise.js';
import { MarktAkquiseStatus } from '../entities/MarktAkquiseStatus.js';
import { MarktVeranstaltungsAkquise } from '../entities/MarktVeranstaltungsAkquise.js';

export namespace MarktAPI {
  /**
   * Ensures that both MarktAkquise and MarktVeranstaltungsAkquise entities exist for a given partner
   * @param partnerID - The ID of the partner to ensure entities for
   * @returns A Promise that resolves to true when entities are ensured
   * @throws Error if the current user is not found
   */
  export const ensureMarktAkquise = registerMutation({
    type: () => Boolean,
    operationName: 'MarktAPI_ensureMarktAkquise',
    resolve: (partnerID: string) => {
      return async () => {
        const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
          roles: { name: 1, permissions: 1, contexts: 1 },
        });

        if (!user) {
          throw new Error('User not found');
        }

        const ma = await loadMongoData(MarktAkquise, { _class: MarktAkquise._class, partnerID });
        const mva = await loadMongoData(MarktVeranstaltungsAkquise, {
          _class: MarktVeranstaltungsAkquise._class,
          partnerID,
        });

        if (ma.length === 0) {
          await new MarktAkquise({
            partnerID,
            status: MarktAkquiseStatus.NEW,
          }).create();
        }

        if (mva.length === 0) {
          await new MarktVeranstaltungsAkquise({
            partnerID,
            status: MarktAkquiseStatus.NEW,
          }).create();
        }

        return true;
      };
    },
    options: {},
    params: [{ type: () => String, name: 'partnerID' }],
  });

  /**
   * Subscribes to changes in MarktAkquise for a specific partner
   * @param partnerID - The ID of the partner to subscribe to
   * @returns An Observable that emits MarktAkquise updates or null if no entity exists
   */
  export const subscribeMarktAkquise = registerSubscription({
    type: () => MarktAkquise,
    operationName: 'MarktAPI_subscribeMarktAkquise',
    resolve: (partnerID: string) => {
      return (gqlFields: BaseQLSelectionSet<MarktAkquise>) => {
        return from(loadMongoData(MarktAkquise, { _class: MarktAkquise._class, partnerID })).pipe(
          switchMap((results: EntityData<MarktAkquise>[]) => {
            if (results.length === 0) return from([null]);
            return subscribeByID(MarktAkquise, results[0]?._id ?? '')(gqlFields) as Observable<MarktAkquise>;
          }),
        );
      };
    },
    params: [{ type: () => String, name: 'partnerID' }],
  });

  /**
   * Subscribes to changes in MarktVeranstaltungsAkquise for a specific partner
   * @param partnerID - The ID of the partner to subscribe to
   * @returns An Observable that emits MarktVeranstaltungsAkquise updates or null if no entity exists
   */
  export const subscribeVeranstaltungsMarktAkquise = registerSubscription({
    type: () => MarktVeranstaltungsAkquise,
    operationName: 'MarktAPI_subscribeVeranstaltungsMarktAkquise',
    resolve: (partnerID: string) => {
      return (gqlFields: BaseQLSelectionSet<MarktVeranstaltungsAkquise>) => {
        return from(
          loadMongoData(MarktVeranstaltungsAkquise, {
            _class: MarktVeranstaltungsAkquise._class,
            partnerID,
          }),
        ).pipe(
          switchMap((results: EntityData<MarktVeranstaltungsAkquise>[]) => {
            if (results.length === 0) return from([null]);
            return subscribeByID(
              MarktVeranstaltungsAkquise,
              results[0]?._id ?? '',
            )(gqlFields) as Observable<MarktVeranstaltungsAkquise>;
          }),
        );
      };
    },
    params: [{ type: () => String, name: 'partnerID' }],
  });

  export const getMarktAkquise = registerQuery({
    type: () => [MarktAkquise],
    operationName: 'MarktAPI_getMarktAkquise',
    resolve: (partnerIDs: string[]) => {
      return async (gqlFields: BaseQLSelectionSet<MarktAkquise>) => {
        return loadMongoData(MarktAkquise, { _class: MarktAkquise._class, partnerID: { $in: partnerIDs } });
      };
    },
    params: [{ type: () => [String], name: 'partnerIDs' }],
  });

  export const getMarktVeranstaltungsAkquise = registerQuery({
    type: () => [MarktVeranstaltungsAkquise],
    operationName: 'MarktAPI_getMarktVeranstaltungsAkquise',
    resolve: (partnerIDs: string[]) => {
      return async (gqlFields: BaseQLSelectionSet<MarktVeranstaltungsAkquise>) => {
        return loadMongoData(MarktVeranstaltungsAkquise, {
          _class: MarktVeranstaltungsAkquise._class,
          partnerID: { $in: partnerIDs },
        });
      };
    },
    params: [{ type: () => [String], name: 'partnerIDs' }],
  });
}
